import {
  Accordion,
  Box,
  Button,
  Center,
  Flex,
  Table,
  Text,
  Textarea,
  Title,
} from "@mantine/core";
import get from "lodash/get";
import { rgba } from "polished";
import { ReactNode, useCallback, useEffect, useState } from "react";

import SvgAlert from "../../../components/Icons/Alert";
import { theme } from "../../../utils/mantine/theme.ts";
import { LoadingMessage } from "../LoadingMessage.tsx";
import {
  DocumentType,
  JSONArray,
  JSONObject,
  JSONPrimitive,
  JSONValue,
} from "../types";
import { UploadDocumentMessage } from "../UploadDocumentMessage.tsx";
import { deepClone, getDataType, isJSONObject, isObject } from "../utils";
import { useStyles } from "./ExtractedDataDisplay.styles";

interface ExtractedDataDisplayProps {
  data: JSONValue | null;
  loading: boolean;
  documentType?: DocumentType;
  documentId?: string | null;
  onSave?: (updatedData: JSONValue, documentType: DocumentType) => void;
  isProccesingFailed?: boolean;
}

const EDITABLE_FIELDS: Record<DocumentType, string[]> = {
  [DocumentType.KTP]: [
    "province",
    "city",
    "nik",
    "name",
    "address",
    "rt_rw",
    "sub_district",
    "district",
    "occupation",
  ],
  [DocumentType.NPWP_COMPANY]: ["npwp_number", "npwp_16", "name", "address"],
  [DocumentType.NPWP_INDIVIDU]: ["npwp_number", "npwp_16", "name", "address"],
  [DocumentType.INVOICE]: [
    "inv_number",
    "inv_issued",
    "inv_due",
    "grand_total_price",
  ],
  [DocumentType.DOKUMEN_PO]: [
    "po_number",
    "po_issued",
    "sender_name",
    "receiver_name",
  ],
  [DocumentType.REKENING_KORAN]: [
    "bank_name",
    "account_name",
    "period.start_date",
    "period.end_date",
  ],
  [DocumentType.FAKTUR_PAJAK]: [],
};

const DOCUMENT_FIELD_ORDER = {
  [DocumentType.KTP]: [
    "province",
    "city",
    "nik",
    "name",
    "birth_place",
    "birth_date",
    "gender",
    "address",
    "rt_rw",
    "sub_district",
    "district",
    "religion",
    "marital",
    "occupation",
    "citizenship",
    "valid_date",
  ],
  [DocumentType.NPWP_COMPANY]: ["npwp_number", "npwp_16", "name", "address"],
  [DocumentType.NPWP_INDIVIDU]: ["npwp_number", "npwp_16", "name", "address"],
  [DocumentType.REKENING_KORAN]: [
    "bank_name",
    "account_name",
    "account_number",
    "address",
    "period",
    "start_date",
    "end_date",
    "currency",
    "transactions",
    "summary",
  ],
  [DocumentType.INVOICE]: [
    "inv_number",
    "inv_issued",
    "inv_due",
    "customer",
    "cust_address",
    "product",
    "total_price",
    "total_terbilang",
    "vat",
    "discount",
    "grand_total_price",
  ],
  [DocumentType.FAKTUR_PAJAK]: [
    "inv_code_and_number",
    "taxable_business",
    "buyer",
    "items",
    "item_code",
    "item_name",
    "unit_price",
    "total_price",
    "discount_price",
    "adv_payment",
    "tax_base",
    "vat_total",
    "total_ppnbm",
  ],
  [DocumentType.DOKUMEN_PO]: [
    "po_number",
    "po_issued",
    "sender_name",
    "receiver_name",
    "product",
    "total_price",
    "total_terbilang",
    "vat",
    "discount",
    "grand_total_price",
  ],
};

const TABLE_COLUMN_ORDER: Record<string, string[]> = {
  transactions: [
    "transaction_date",
    "description",
    "reference",
    "debit",
    "credit",
    "balance",
  ],
  product: ["product_name", "quantity", "unit_price", "amount"],
  items: ["item_code", "item_name", "unit_price", "total_price"],
  taxable_business: ["name", "address", "npwp"],
  buyer: ["name", "address", "npwp"],
  period: ["start_date", "end_date"],
  summary: ["total_debit", "total_credit", "final_balance"],
};

const ExtractedDataDisplay = ({
  data,
  loading,
  documentType,
  onSave,
  isProccesingFailed,
}: ExtractedDataDisplayProps) => {
  const { classes } = useStyles();
  const [editedData, setEditedData] = useState<JSONValue | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (data) {
      setEditedData(deepClone(data));
      setHasChanges(false);
    }
  }, [data]);

  const isFieldEditable = useCallback(
    (fieldPath: string): boolean => {
      if (!documentType) return false;
      const editableFields = EDITABLE_FIELDS[documentType] || [];
      return editableFields.includes(fieldPath);
    },
    [documentType]
  );

  const updateFieldValue = useCallback(
    (fieldPath: string, value: string) => {
      if (!editedData || !isJSONObject(editedData)) return;

      const pathParts = fieldPath.split(".");
      const updatedData = deepClone(editedData) as JSONObject;

      let current: JSONObject = updatedData;
      for (let i = 0; i < pathParts.length - 1; i++) {
        if (!current[pathParts[i]] || !isJSONObject(current[pathParts[i]])) {
          current[pathParts[i]] = {};
        }
        current = current[pathParts[i]] as JSONObject;
      }

      current[pathParts[pathParts.length - 1]] = value;

      setEditedData(updatedData);
      setHasChanges(true);
    },
    [editedData]
  );

  const handleSave = () => {
    if (editedData && onSave && documentType) {
      onSave(editedData, documentType);
      setHasChanges(false);
    }
  };

  const handleReset = () => {
    if (data) {
      setEditedData(deepClone(data));
      setHasChanges(false);
    }
  };

  if (isProccesingFailed) {
    return (
      <Center className={classes.emptyState}>
        <SvgAlert width={80} height={80} style={{ color: "#F44336" }} />
        <Text
          className={classes.emptyText}
          align="center"
          style={{ color: "#F44336" }}
        >
          DOCUMENT PROCESSING FAILED
        </Text>
        <Text size="sm" align="center" color="dimmed">
          Please try uploading the document again or contact support if the
          issue persists
        </Text>
      </Center>
    );
  }

  if (loading) {
    return <LoadingMessage />;
  }

  if (!editedData) {
    return <UploadDocumentMessage />;
  }

  const duskColor = get(theme, "colors.dusk.0", "dusk");

  return (
    <Box className={classes.container}>
      {hasChanges && (
        <Flex
          justify="space-between"
          align="center"
          mb="md"
          p="md"
          style={{
            backgroundColor: rgba(duskColor, 0.1),
            borderRadius: "4px",
            border: `1px solid ${duskColor}`,
          }}
        >
          <Text size="sm" color={duskColor}>
            You have unsaved changes
          </Text>
          <Flex gap="sm">
            <Button size="xs" variant="outlineBlue" onClick={handleReset}>
              Reset
            </Button>
            <Button size="xs" variant="filledBlue" onClick={handleSave}>
              Save Changes
            </Button>
          </Flex>
        </Flex>
      )}

      <Flex direction="column" gap="md">
        {renderData(
          editedData,
          classes,
          isFieldEditable,
          updateFieldValue,
          "",
          documentType
        )}
      </Flex>
    </Box>
  );
};

const shouldRenderAsTable = (array: JSONArray): boolean => {
  if (array.length === 0) return false;
  return array.every((item) => isObject(item));
};

const getTableHeaders = (array: JSONArray, tableKey?: string): string[] => {
  if (array.length === 0) return [];

  if (tableKey && tableKey in TABLE_COLUMN_ORDER) {
    const firstRow = array[0];
    if (isObject(firstRow)) {
      const availableColumns = Object.keys(firstRow);
      const predefinedOrder = TABLE_COLUMN_ORDER[tableKey];
      const orderedColumns = predefinedOrder.filter((col: string) =>
        availableColumns.includes(col)
      );
      const additionalColumns = availableColumns.filter(
        (col: string) => !predefinedOrder.includes(col)
      );
      return [...orderedColumns, ...additionalColumns];
    }
  }

  const allKeys = new Set<string>();
  array.forEach((item) => {
    if (isObject(item)) {
      Object.keys(item).forEach((key) => allKeys.add(key));
    }
  });

  return Array.from(allKeys);
};

const renderPrimitive = (
  key: string,
  value: JSONPrimitive,
  isEditable: (fieldPath: string) => boolean,
  onUpdate: (fieldPath: string, value: string) => void,
  fieldPath: string = key
): ReactNode => {
  const displayValue = value?.toString() || "";
  const editable = isEditable(fieldPath);

  return (
    <Textarea
      label={formatFieldName(key)}
      required={editable}
      value={displayValue}
      readOnly={!editable}
      disabled={!editable}
      autosize
      minRows={1}
      maxRows={4}
      onChange={(e) => editable && onUpdate(fieldPath, e.target.value)}
    />
  );
};

const renderTable = (
  key: string,
  array: JSONArray,
  classes: ReturnType<typeof useStyles>["classes"],
  isEditable: (fieldPath: string) => boolean,
  onUpdate: (fieldPath: string, value: string) => void,
  parentPath = ""
): ReactNode => {
  const currentPath = parentPath ? `${parentPath}.${key}` : key;

  if (!shouldRenderAsTable(array)) {
    return (
      <Accordion key={key} defaultValue="closed" className={classes.accordion}>
        <Accordion.Item value={key}>
          <Accordion.Control>
            <Title order={5}>{formatFieldName(key).toUpperCase()}</Title>
          </Accordion.Control>
          <Accordion.Panel>
            <Flex direction="column" gap="md">
              {array.map((item, index) => (
                <Box key={index}>
                  <Text weight={500} size="sm" mb="xs">
                    Item {index + 1}
                  </Text>
                  {isObject(item)
                    ? renderData(
                        item,
                        classes,
                        isEditable,
                        onUpdate,
                        `${currentPath}[${index}]`
                      )
                    : renderPrimitive(
                        `item-${index}`,
                        item as JSONPrimitive,
                        isEditable,
                        onUpdate,
                        `${currentPath}[${index}]`
                      )}
                </Box>
              ))}
            </Flex>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    );
  }

  const headers = getTableHeaders(array, key);

  return (
    <Accordion key={key} defaultValue={key} className={classes.accordion}>
      <Accordion.Item value={key}>
        <Accordion.Control>
          <Title order={5}>{formatFieldName(key).toUpperCase()}</Title>
        </Accordion.Control>
        <Accordion.Panel>
          <Table striped className={classes.table}>
            <thead>
              <tr>
                {headers.map((header) => (
                  <th key={header}>{formatFieldName(header)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {array.map((row, index) => (
                <tr key={index}>
                  {headers.map((header) => (
                    <td key={header}>
                      {isObject(row) ? renderTableCell(row[header]) : ""}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
};

const renderTableCell = (value: JSONValue): string => {
  if (!value) return "";

  if (isObject(value) || Array.isArray(value)) return JSON.stringify(value);

  return value.toString();
};

const renderNestedObject = (
  key: string,
  obj: JSONObject,
  classes: ReturnType<typeof useStyles>["classes"],
  isEditable: (fieldPath: string) => boolean,
  onUpdate: (fieldPath: string, value: string) => void,
  parentPath = "",
  documentType?: DocumentType
) => {
  const currentPath = parentPath ? `${parentPath}.${key}` : key;

  return (
    <Flex key={key} direction="column" gap="sm">
      <Text size="md" weight={600}>
        {formatFieldName(key)}
      </Text>
      <Flex direction="column" gap="sm" ml="sm">
        {renderData(
          obj,
          classes,
          isEditable,
          onUpdate,
          currentPath,
          documentType
        )}
      </Flex>
    </Flex>
  );
};

const renderData = (
  data: JSONValue,
  classes: ReturnType<typeof useStyles>["classes"],
  isEditable: (fieldPath: string) => boolean,
  onUpdate: (fieldPath: string, value: string) => void,
  parentPath = "",
  documentType?: DocumentType
) => {
  if (!isObject(data)) return [];

  const elements: ReactNode[] = [];

  const getOrderedFields = (dataObj: JSONObject): string[] => {
    if (!parentPath && documentType) {
      const fieldOrder = DOCUMENT_FIELD_ORDER[documentType] || [];
      const existingFields = Object.keys(dataObj);

      const orderedFields = fieldOrder.filter((field) =>
        existingFields.includes(field)
      );
      const additionalFields = existingFields.filter(
        (field) => !fieldOrder.includes(field)
      );

      return [...orderedFields, ...additionalFields];
    }

    const currentObjectKey = parentPath.split(".").pop();
    if (currentObjectKey && currentObjectKey in TABLE_COLUMN_ORDER) {
      const fieldOrder = TABLE_COLUMN_ORDER[currentObjectKey];
      const existingFields = Object.keys(dataObj);

      const orderedFields = fieldOrder.filter((field) =>
        existingFields.includes(field)
      );
      const additionalFields = existingFields.filter(
        (field) => !fieldOrder.includes(field)
      );

      return [...orderedFields, ...additionalFields];
    }

    return Object.keys(dataObj);
  };

  const orderedKeys = getOrderedFields(data);

  orderedKeys.forEach((key) => {
    const value = data[key];
    const currentPath = parentPath ? `${parentPath}.${key}` : key;
    const dataType = getDataType(value);

    switch (dataType) {
      case "primitive":
        elements.push(
          renderPrimitive(
            key,
            value as JSONPrimitive,
            isEditable,
            onUpdate,
            currentPath
          )
        );
        break;

      case "array":
        elements.push(
          renderTable(
            key,
            value as JSONArray,
            classes,
            isEditable,
            onUpdate,
            parentPath
          )
        );
        break;

      case "object":
        elements.push(
          renderNestedObject(
            key,
            value as JSONObject,
            classes,
            isEditable,
            onUpdate,
            parentPath,
            documentType
          )
        );
        break;

      case "null":
        elements.push(
          renderPrimitive(key, null, isEditable, onUpdate, currentPath)
        );
        break;

      default:
        elements.push(
          renderPrimitive(key, String(value), isEditable, onUpdate, currentPath)
        );
    }
  });

  return elements;
};

const formatFieldName = (key: string) => {
  const specialCases: Record<string, string> = {
    nik: "NIK",
    npwp: "NPWP",
    npwp_number: "NPWP Number",
    npwp_16: "NPWP 16-Digit",
    rtRw: "RT/RW",
    rt_rw: "RT/RW",
    kelDesa: "Kel/Desa",
    inv_number: "Invoice Number",
    inv_issued: "Invoice Issued Date",
    inv_due: "Invoice Due Date",
    cust_address: "Customer Address",
    product_name: "Product Name",
    unit_price: "Unit Price",
    sub_total_price: "Sub Total Price",
    total_terbilang: "Total (Terbilang)",
    grand_total_price: "Grand Total Price",
    tax_base: "Tax Base",
    vat_total: "VAT Total",
    total_ppnbm: "Total PPnBM",
    adv_payment: "Advance Payment",
    discount_price: "Discount Price",
    invoice_code_and_number: "Invoice Code and Number",
    item_code: "Item Code",
    item_name: "Item Name",
    transaction_date: "Transaction Date",
    account_name: "Account Name",
    account_number: "Account Number",
    bank_name: "Bank Name",
    start_date: "Start Date",
    end_date: "End Date",
    total_debit: "Total Debit",
    total_credit: "Total Credit",
    total_num_debit: "Total Number of Debits",
    total_num_credit: "Total Number of Credits",
    final_balance: "Final Balance",
    birth_place: "Birth Place",
    birth_date: "Birth Date",
    valid_date: "Valid Date",
    sub_district: "Sub District",
    po_number: "PO Number",
    po_issued: "PO Issued Date",
    sender_name: "Sender Name",
    receiver_name: "Receiver Name",
    province: "Province",
    city: "City",
    name: "Name",
    address: "Address",
    district: "District",
    occupation: "Occupation",
    customer: "Customer",
    vat: "VAT",
    discount: "Discount",
    gender: "Gender",
    religion: "Religion",
    marital: "Marital Status",
    citizenship: "Citizenship",
    currency: "Currency",
    period: "Period",
    transactions: "Transactions",
    product: "Product",
    quantity: "Quantity",
    amount: "Amount",
    description: "Description",
    reference: "Reference",
    debit: "Debit",
    credit: "Credit",
    balance: "Balance",
    taxable_business: "Taxable Business",
    buyer: "Buyer",
    items: "Items",
    total_price: "Total Price",
  };

  if (specialCases[key]) {
    return specialCases[key];
  }

  if (key.includes("_")) {
    return key
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }

  const result = key.replace(/([A-Z])/g, " $1");
  return result.charAt(0).toUpperCase() + result.slice(1);
};

export default ExtractedDataDisplay;
